import { makeAutoObservable } from "mobx";
import { IChangeable } from "../../../stores/types";
import {
    EXCHANGE_ORDER_SHIPPING_OPTIONS,
} from "../components/resolution/resolution-type-viewer/exchange/constants";
import PlatformServiceStore, {FUNC} from "../../../stores/platform-service-store";
import {IOption} from "../../../components/inputs/select-dropdown/select-dropdown";
import {
    FALLBACK_SHIPPING_TYPE,
    RATELESS_SHIPPING_LINE_TYPES,
    SHIPPING_LINE_TYPE,
} from "../constants";

export interface IShippingSettings {
    id: number;
    shipping_line: SHIPPING_LINE_TYPE | null;
    shipping_line_title: string | null;
    fallback_shipping_line: FALLBACK_SHIPPING_TYPE | null;
}

export interface IShippingSettingsChanges extends Partial<IShippingSettings> {}

export class ShippingSettingsStore implements IChangeable<IShippingSettings> {
    private _model?: IShippingSettings;
    private _changes: IShippingSettingsChanges = {};
  
    constructor(model?: IShippingSettings) {
        makeAutoObservable(this, {}, { autoBind: true });
        this.setModel(model);
    }
    
    public get shippingLine(): SHIPPING_LINE_TYPE | null | undefined { return this._changes.shipping_line ?? this._model?.shipping_line; }
    public get shippingLineTitle(): string | null | undefined { return this._changes.shipping_line_title ?? this._model?.shipping_line_title; }
    public get fallbackShippingLine(): FALLBACK_SHIPPING_TYPE | null | undefined { return this._changes.fallback_shipping_line ?? this._model?.fallback_shipping_line; }

    public getFilteredShippingLineOptions(platform?: PlatformServiceStore): IOption[] {
        if (platform?.isFunctionalitySupported(FUNC.FUNC_DRAFT_ORDER_SHIPPING_RATES)) {
            return EXCHANGE_ORDER_SHIPPING_OPTIONS;
        }
        
        return EXCHANGE_ORDER_SHIPPING_OPTIONS.filter(option => RATELESS_SHIPPING_LINE_TYPES.includes(option.value));
    }

    public setModel(model?: IShippingSettings): void {
        this._model = model;
        this._changes = {};
    }

    public setShippingLine(value: SHIPPING_LINE_TYPE | null): void {
        this._changes.shipping_line = value;

        if ([SHIPPING_LINE_TYPE.SELECT_FIRST_FREE_SHIPPING, SHIPPING_LINE_TYPE.LEAVE_BLANK].includes(value!)) {
            this._changes.shipping_line_title = undefined;
            this._changes.fallback_shipping_line = undefined;
        }

        this._clearUnneededChanges();
    }

    public setShippingLineTitle(value: string | null): void {
        this._changes.shipping_line_title = value;
        this._clearUnneededChanges();
    }

    public setFallbackShippingLine(value: FALLBACK_SHIPPING_TYPE | null): void {
        this._changes.fallback_shipping_line = value;
        this._clearUnneededChanges();
    }

    public hasChanges(): boolean {
        return !!Object.keys(this._changes).length;
    }

    public discardChanges(): void {
        this._changes = {};
    }

    public getUpdatedModel(): IShippingSettings {
        return { ...this._model, ...this._changes as IShippingSettings };
    }

    private _clearUnneededChanges(): void {
        Object.keys(this._changes).forEach((key) => {
            const changedKey = key as keyof IShippingSettingsChanges;
            if (this._changes[changedKey] === this._model?.[changedKey as keyof IShippingSettings]) {
                delete this._changes[changedKey];
            }
        });
    }
}
